import { DEFAULT_OTP_RESEND_AFTER_SECONDS } from '@/components/OtpEntry/constants'

import { PRODUCT_OPERATIONS } from '../operations'

const withCountryCode = phone => (phone?.startsWith('+91') ? phone : `+91${phone || ''}`)

const parseResendSeconds = (raw, fallback = DEFAULT_OTP_RESEND_AFTER_SECONDS) => {
  const data = raw?.data ?? raw ?? {}
  const value =
    data.can_resend_after_seconds ?? data.can_resend_after ?? data.expires_in_seconds ?? fallback
  const n = Number.parseInt(String(value), 10)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

const responseMessage = raw => (typeof raw === 'object' && raw !== null ? raw.message : undefined)

const emailOtpRequestData = ({ email }) => ({ email_id: email })

const emailOtpResponse = raw => {
  const data = raw?.data ?? {}
  return {
    ok: !!raw?.success,
    message: raw?.message,
    expiresInSeconds: data.expires_in_seconds,
    canResendAfterSeconds: data.can_resend_after_seconds,
    otpId: raw?.otpId ?? data.otpId,
    raw
  }
}

const DEFAULT_CL_HEADERS = {}

/** Static or input-based header overrides for edge cases. */
const CL_HEADER_OVERRIDES = {
  [PRODUCT_OPERATIONS.SEND_OTP]: { accept: 'application/json' },
  [PRODUCT_OPERATIONS.RAISE_COMPLAINT]: input =>
    input.idempotencyKey ? { 'Idempotency-Key': input.idempotencyKey } : {}
}

const resolveHeaderOverride = (operation, input) => {
  const override = CL_HEADER_OVERRIDES[operation]
  if (!override) return {}
  return typeof override === 'function' ? override(input) : override
}

/** One entry per CL operation — HTTP method, wire request, normalized response. */
const CL_OPERATION_CONFIG = {
  [PRODUCT_OPERATIONS.AUTHENTICATE]: {
    method: 'POST',
    requestData: () => ({}),
    response: raw => ({ isActive: !!raw?.status, muid: raw?.muid })
  },
  [PRODUCT_OPERATIONS.SEND_OTP]: {
    method: 'POST',
    requestData: ({ phone }) => ({ phone }),
    response: raw => ({ ok: true, raw })
  },
  [PRODUCT_OPERATIONS.VERIFY_LOGIN]: {
    method: 'POST',
    requestData: ({ code, phone }) => ({ code, phone: withCountryCode(phone) }),
    response: raw => ({
      ok: true,
      muid: raw?.data?.muid ?? raw?.muid ?? null,
      raw
    })
  },
  [PRODUCT_OPERATIONS.GENERATE_EMAIL_OTP]: {
    method: 'POST',
    requestData: emailOtpRequestData,
    response: emailOtpResponse
  },
  [PRODUCT_OPERATIONS.RESEND_EMAIL_OTP]: {
    method: 'POST',
    requestData: emailOtpRequestData,
    response: emailOtpResponse
  },
  [PRODUCT_OPERATIONS.VERIFY_EMAIL_OTP]: {
    method: 'POST',
    requestData: ({ email, otp }) => ({ email_id: email, otp }),
    response: raw => {
      const data = raw?.data ?? {}
      return {
        ok: !!raw?.success,
        message: raw?.message,
        email: data.email,
        verifiedAt: data.verified_at,
        verificationStatus: data.verification_status,
        raw
      }
    }
  },
  [PRODUCT_OPERATIONS.SEND_COMPLAINT_OTP]: {
    method: 'POST',
    requestData: ({ email }) => ({ email }),
    response: raw => ({
      ok: true,
      resendAfterSeconds: parseResendSeconds(raw),
      raw
    })
  },
  [PRODUCT_OPERATIONS.RAISE_COMPLAINT]: {
    method: 'POST',
    requestData: ({ email, otp, ticket }) => ({ email, otp, ticket }),
    response: raw => ({
      ok: true,
      message: responseMessage(raw),
      raw
    })
  }
}

const getConfig = operation => {
  const config = CL_OPERATION_CONFIG[operation]
  if (!config) {
    throw new Error(`Operation "${operation}" is not supported for CL`)
  }
  return config
}

export const clAdapter = {
  buildHeaders: (_config, operation, input = {}) => ({
    ...DEFAULT_CL_HEADERS,
    ...resolveHeaderOverride(operation, input)
  }),
  buildRequest: (operation, input = {}) => getConfig(operation).requestData(input),
  normalizeResponse: (operation, raw) => getConfig(operation).response(raw),
  getHttpMethod: operation => getConfig(operation).method
}
